import { useEffect, useRef, useCallback } from 'react';
import { SameHeight } from '../utils';

/**
 * Custom hook for automatic height adjustment when components are added/removed
 * 
 * This hook uses MutationObserver to detect DOM changes and automatically
 * refreshes the SameHeight utility when new components are added or removed.
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.containerSelector - CSS selector for the container element
 * @param {string} options.elementSelector - CSS selector for elements that should have equal heights
 * @param {number} options.debounceDelay - Delay for debouncing height updates (default: 200ms)
 * @param {boolean} options.observeChildList - Whether to observe child list changes (default: true)
 * @param {boolean} options.observeAttributes - Whether to observe attribute changes (default: true)
 * @param {boolean} options.observeCharacterData - Whether to observe text content changes (default: true)
 * @param {Array} options.attributeFilter - Specific attributes to observe (default: null - observe all)
 * @param {boolean} options.subtree - Whether to observe the entire subtree (default: true)
 * @returns {Object} - { sameHeightRef, refresh, destroy }
 */
const useAutoHeightAdjustment = (options = {}) => {
  const {
    containerSelector = '.same-height',
    elementSelector = '[data-same-height]',
    debounceDelay = 200,
    observeChildList = true,
    observeAttributes = true,
    observeCharacterData = true,
    attributeFilter = null,
    subtree = true
  } = options;

  const sameHeightRef = useRef(null);
  const observerRef = useRef(null);
  const refreshTimeoutRef = useRef(null);
  const isInitializedRef = useRef(false);

  // Debounced refresh function
  const debouncedRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    refreshTimeoutRef.current = setTimeout(() => {
      if (sameHeightRef.current && typeof sameHeightRef.current.refresh === 'function') {
        sameHeightRef.current.refresh();
      }
    }, debounceDelay);
  }, [debounceDelay]);

  // Initialize SameHeight utility
  const initializeSameHeight = useCallback(() => {
    if (sameHeightRef.current && typeof sameHeightRef.current.destroy === 'function') {
      sameHeightRef.current.destroy();
    }

    sameHeightRef.current = new SameHeight({
      debounceDelay,
      containerSelector,
      elementSelector,
      minHeight: null,
      autoInit: true
    });

    isInitializedRef.current = true;
  }, [containerSelector, elementSelector, debounceDelay]);

  // Setup MutationObserver
  const setupObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const containers = document.querySelectorAll(containerSelector);
    if (containers.length === 0) {
      return;
    }

    const observerOptions = {
      childList: observeChildList,
      attributes: observeAttributes,
      characterData: observeCharacterData,
      subtree: subtree
    };

    if (attributeFilter && Array.isArray(attributeFilter)) {
      observerOptions.attributeFilter = attributeFilter;
    }

    observerRef.current = new MutationObserver((mutations) => {
      let shouldRefresh = false;

      mutations.forEach((mutation) => {
        // Check if any relevant changes occurred
        if (mutation.type === 'childList') {
          // Check if any added/removed nodes have the same-height attribute
          const addedNodes = Array.from(mutation.addedNodes).filter(
            node => node.nodeType === Node.ELEMENT_NODE && 
            (node.matches && node.matches(elementSelector) || 
             node.querySelector && node.querySelector(elementSelector))
          );
          
          const removedNodes = Array.from(mutation.removedNodes).filter(
            node => node.nodeType === Node.ELEMENT_NODE && 
            (node.matches && node.matches(elementSelector) || 
             node.querySelector && node.querySelector(elementSelector))
          );

          // Also check for service tags or other dynamic content
          const addedServiceTags = Array.from(mutation.addedNodes).filter(
            node => node.nodeType === Node.ELEMENT_NODE && 
            (node.classList && (node.classList.contains('service-tag') || 
             node.classList.contains('selected-services') ||
             node.querySelector && node.querySelector('.service-tag')))
          );
          
          const removedServiceTags = Array.from(mutation.removedNodes).filter(
            node => node.nodeType === Node.ELEMENT_NODE && 
            (node.classList && (node.classList.contains('service-tag') || 
             node.classList.contains('selected-services') ||
             node.querySelector && node.querySelector('.service-tag')))
          );

          if (addedNodes.length > 0 || removedNodes.length > 0 || 
              addedServiceTags.length > 0 || removedServiceTags.length > 0) {
            shouldRefresh = true;
          }
        } else if (mutation.type === 'attributes') {
          // Check if the attribute change affects same-height functionality
          if (mutation.attributeName === 'data-same-height' || 
              mutation.attributeName === 'same-height-device' ||
              mutation.attributeName === 'class') {
            shouldRefresh = true;
          }
        } else if (mutation.type === 'characterData') {
          // Text content changes might affect height
          shouldRefresh = true;
        }
      });

      if (shouldRefresh) {
        debouncedRefresh();
      }
    });

    // Observe all containers
    containers.forEach(container => {
      observerRef.current.observe(container, observerOptions);
    });
  }, [
    containerSelector, 
    elementSelector, 
    observeChildList, 
    observeAttributes, 
    observeCharacterData, 
    attributeFilter, 
    subtree, 
    debouncedRefresh
  ]);

  // Manual refresh function
  const refresh = useCallback(() => {
    if (sameHeightRef.current && typeof sameHeightRef.current.refresh === 'function') {
      sameHeightRef.current.refresh();
    }
  }, []);

  // Destroy function
  const destroy = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    if (sameHeightRef.current && typeof sameHeightRef.current.destroy === 'function') {
      sameHeightRef.current.destroy();
      sameHeightRef.current = null;
    }

    isInitializedRef.current = false;
  }, []);

  // Initialize on mount
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const initTimer = setTimeout(() => {
      initializeSameHeight();
      setupObserver();
    }, 100);

    return () => {
      clearTimeout(initTimer);
      destroy();
    };
  }, [initializeSameHeight, setupObserver, destroy]);

  // Re-setup observer when options change
  useEffect(() => {
    if (isInitializedRef.current) {
      setupObserver();
    }
  }, [setupObserver]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      debouncedRefresh();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [debouncedRefresh]);

  return {
    sameHeightRef,
    refresh,
    destroy,
    isInitialized: isInitializedRef.current
  };
};

export default useAutoHeightAdjustment;
